// Loaded only by a product that explicitly enables style details.
if (!customElements.get('scoreggia-style-description')) {
  customElements.define('scoreggia-style-description', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.root = this.closest('product-info');
      if (!this.root) return;
      this.controller = new AbortController();
      this.currentTemplate = null;
      this.pendingTemplate = null;
      this.preparedTemplates = new Map();
      this.querySelectorAll('template').forEach((template) => this.prepareTemplate(template));
      const update = () => this.updateDescription();
      for (const event of ['change', 'product-info:loaded', 'product-info:variant-rendered']) {
        this.root.addEventListener(event, update, { signal: this.controller.signal });
      }
      // Variant rendering replaces the picker. Observe only picker replacements,
      // never rewrite or intercept any commerce controls or their events.
      this.observer = new MutationObserver((records) => {
        if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some(
          (node) => node.nodeType === 1 && (node.matches('variant-selects') || node.querySelector('variant-selects'))
        ))) update();
      });
      this.observer.observe(this.root, { childList: true, subtree: true });
      update();
    }

    prepareTemplate(template) {
      if (this.preparedTemplates.has(template)) return this.preparedTemplates.get(template);
      // Import into the live document: template.content belongs to an inert
      // document, where detached image decoding can reject before loading.
      const fragment = this.ownerDocument.importNode(template.content, true);
      const images = Array.from(fragment.querySelectorAll('img'));
      const prepared = { nodes: Array.from(fragment.childNodes), ready: images.length === 0 };
      prepared.promise = Promise.all(images.map(async (image) => {
        // Decode off-screen before replacing the visible description. Reuse these
        // actual image nodes so revisiting a style never restarts lazy loading.
        image.loading = 'eager';
        try { await image.decode(); } catch { /* Broken images must not block text. */ }
        if (image.naturalWidth && image.naturalHeight && !image.hasAttribute('width') && !image.hasAttribute('height')) {
          image.setAttribute('width', image.naturalWidth);
          image.setAttribute('height', image.naturalHeight);
        }
      })).then(() => { prepared.ready = true; });
      this.preparedTemplates.set(template, prepared);
      return prepared;
    }

    updateDescription() {
      const selected = this.root.querySelector(
        'variant-selects [data-scoreggia-option="style"] input:checked, ' +
        'variant-selects [data-scoreggia-option="style"] select option:checked'
      );
      const template = Array.from(this.querySelectorAll('template[data-style-description-value]'))
        .find((candidate) => candidate.dataset.styleDescriptionValue === selected?.value)
        || this.querySelector('template[data-style-description-fallback]');
      const content = this.querySelector('[data-style-description-content]');
      if (!content || !template) return;
      // Ignore a transient empty picker during server replacement. A real style
      // with missing content still uses the merchant's fallback description.
      if (!selected && this.currentTemplate) return;
      this.pendingTemplate = template;
      if (template === this.currentTemplate) return;
      const prepared = this.prepareTemplate(template);
      const controller = this.controller;
      const commit = () => {
        if (controller.signal.aborted || this.pendingTemplate !== template || this.currentTemplate === template) return;
        content.replaceChildren(...prepared.nodes);
        this.currentTemplate = template;
      };
      if (prepared.ready) commit();
      else prepared.promise.then(commit);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
      this.root = null;
    }
  });
}
